import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, DocumentTextIcon, MusicalNoteIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

interface AttachmentCardProps {
  /** 已上传的文件名，空字符串表示未上传 */
  fileName: string;
  uploading: boolean;
  disabled?: boolean;
  required?: boolean;
  /** 卡片图标类型 */
  iconType?: 'document' | 'music';
  /** 未上传时显示的标签 */
  label: string;
  /** hover 时显示的说明文字 */
  tooltip: string;
  onUploadClick: () => void;
  onClear: () => void;
}

export default function AttachmentCard({
  fileName, uploading, disabled = false, required = false,
  iconType = 'document', label, tooltip,
  onUploadClick, onClear,
}: AttachmentCardProps) {
  const { t } = useTranslation('script');
  const [showTooltip, setShowTooltip] = useState(false);

  const Icon = iconType === 'music' ? MusicalNoteIcon : DocumentTextIcon;
  const iconColor = iconType === 'music' ? 'text-purple-400' : 'text-primary';
  const borderColor = required
    ? (fileName ? 'border-purple-500/60' : 'border-dashed border-primary/50')
    : 'border-dashed border-border-subtle';

  return (
    <div className="relative shrink-0">
      {/* 主卡片 */}
      {fileName ? (
        <div
          className={`flex flex-col items-center justify-center gap-1.5 w-20 h-24 px-2 bg-bg-elevated rounded-lg border ${required ? 'border-purple-500/60' : 'border-border-subtle'} cursor-default`}
        >
          <Icon className={`w-5 h-5 ${iconColor} shrink-0`} />
          <span className="text-[10px] text-gray-300 truncate w-full text-center leading-tight px-1">
            {fileName}
          </span>
          <button
            onClick={onClear}
            disabled={disabled}
            className="text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
            title={t('attachmentCard.removeFile')}
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={onUploadClick}
          disabled={uploading || disabled}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className={`flex flex-col items-center justify-center gap-1.5 w-20 h-24 bg-bg-elevated hover:bg-bg-surface text-gray-300 rounded-lg border ${borderColor} transition-colors disabled:opacity-50 cursor-pointer group`}
        >
          {uploading ? (
            <div className={`w-5 h-5 border-2 ${iconType === 'music' ? 'border-purple-400' : 'border-primary'} border-t-transparent rounded-full animate-spin`} />
          ) : (
            <Icon className={`w-5 h-5 ${iconColor} group-hover:scale-110 transition-transform`} />
          )}
          <span className="text-[10px] leading-tight text-center px-1">
            {uploading ? t('attachmentCard.uploading') : label}
          </span>
          {required && (
            <span className="text-[9px] text-primary/70 leading-none">{t('attachmentCard.required')}</span>
          )}
          {!required && (
            <span className="text-[9px] text-gray-400 leading-none">{t('attachmentCard.optional')}</span>
          )}
        </button>
      )}

      {/* Tooltip 说明 */}
      {showTooltip && !fileName && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[9999] w-52 pointer-events-none">
          <div className="bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 shadow-xl">
            <div className="flex items-start gap-1.5">
              <InformationCircleIcon className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-300 leading-relaxed">{tooltip}</p>
            </div>
          </div>
          {/* 小三角 */}
          <div className="flex justify-center">
            <div className="w-2 h-2 bg-bg-elevated border-r border-b border-border-subtle rotate-45 -mt-1" />
          </div>
        </div>
      )}
    </div>
  );
}
